import { useEffect, useRef, useState } from "react";

export interface MeasureRect {
  width: number;
  height: number;
}

/**
 * Track the size of an element via `ResizeObserver`. Returns a tuple of
 * `[ref, rect]` — pass the ref to your element, read `rect.width` / `rect.height`
 * during render.
 *
 *   const [ref, { width, height }] = useMeasure<HTMLDivElement>();
 *   <div ref={ref}>...</div>
 *
 * SSR-safe: returns 0/0 until mount.
 */
export function useMeasure<T extends Element = Element>(): readonly [
  React.RefCallback<T>,
  MeasureRect,
] {
  const [rect, setRect] = useState<MeasureRect>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const setRef: React.RefCallback<T> = (node) => {
    observerRef.current?.disconnect();
    if (!node || typeof window === "undefined" || !("ResizeObserver" in window)) {
      return;
    }
    observerRef.current = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // Only emit a new object when the size actually changed — the ref callback
      // re-registers the observer on every render, so without this guard an
      // observer that reports the same size would churn renders needlessly.
      setRect((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      );
    });
    observerRef.current.observe(node);
  };

  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  return [setRef, rect] as const;
}
